import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Sidebar from "./Sidebar";
import Navbar from "./Navbar";
import toast, { Toaster } from "react-hot-toast";
import {
  FiArrowLeft,
  FiChevronRight,
  FiUser,
  FiBell,
  FiMail,
  FiZap,
  FiShare2,
  FiSmartphone,
  FiExternalLink,
  FiCopy,
  FiCheck,
  FiX,
  FiSend,
  FiTrash2,
  FiAlertCircle,
  FiRefreshCw,
} from "react-icons/fi";
import SendPushModal from "./SendPushModal";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const Field = ({ label, value, mono = false }) => (
  <div>
    <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-1">
      {label}
    </p>
    <p
      className={`text-sm font-medium text-gray-800 break-all ${
        mono ? "font-mono text-xs bg-gray-50 px-2 py-1 rounded inline-block" : ""
      }`}
    >
      {value ?? "—"}
    </p>
  </div>
);

const UserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("Account");
  const [copied, setCopied] = useState(false);

  // Send Notification Modal
  const [showNotifyModal, setShowNotifyModal] = useState(false);

  // Send Email Modal
  const [showMailModal, setShowMailModal] = useState(false);
  const [mailSubject, setMailSubject] = useState("");
  const [mailContent, setMailContent] = useState("");
  const [mailLoading, setMailLoading] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState("general");

  const EMAIL_TEMPLATES = {
    general: {
      label: "✉️ General Notice",
      subject: (u) => `Notice from Zeta Network - Account #${u?.userId || ""}`,
      content: (u) => `Hello ${u?.username || "User"},

We are reaching out to you regarding your Zeta Network account (#${u?.userId || ""}).

If you have any questions or require assistance with mining or rewards, please feel free to reach out to our team.

Best regards,
Zeta Admin Team`,
    },
    welcome: {
      label: "🎉 Welcome Message",
      subject: (u) => `Welcome to Zeta Network - Account #${u?.userId || ""}`,
      content: (u) => `Hello ${u?.username || "Pioneer"},

Welcome to Zeta Network! We are thrilled to have you as part of our mining community.

Start your daily mining cycles, invite friends with your referral code (${u?.referralCode || u?.uniqueCode || "—"}), and participate in live community events to maximize your ZX rewards.

Best regards,
Zeta Admin Team`,
    },
    warning: {
      label: "⚠️ Warning Notice",
      subject: (u) => `Important Notice: Account Compliance Policy - #${u?.userId || ""}`,
      content: (u) => `Hello ${u?.username || "User"},

Our system has flagged potential irregular activity or duplicate accounts associated with your device or IP address.

Please ensure you adhere to our Terms of Service (one account per individual). Failure to comply may lead to strike penalties or account restrictions.

If you believe this is an error, please contact support.

Best regards,
Zeta Compliance Team`,
    },
    rewards: {
      label: "🪙 Rewards & Bonus",
      subject: (u) => `Zeta Network: Rewards & Community Update - #${u?.userId || ""}`,
      content: (u) => `Hello ${u?.username || "User"},

We have exciting updates regarding our event leaderboards and token rewards on Zeta Network!

Check out the latest community events and leaderboards in the app to claim your bonuses.

Happy Mining!
Zeta Admin Team`,
    },
  };

  const handleOpenMailModal = () => {
    if (!user) return;
    if (!user.email) {
      toast.error("User does not have a registered email address");
      return;
    }
    const tpl = EMAIL_TEMPLATES.general;
    setMailSubject(tpl.subject(user));
    setMailContent(tpl.content(user));
    setActiveTemplate("general");
    setShowMailModal(true);
  };

  const handleApplyTemplate = (key) => {
    if (!EMAIL_TEMPLATES[key] || !user) return;
    setActiveTemplate(key);
    setMailSubject(EMAIL_TEMPLATES[key].subject(user));
    setMailContent(EMAIL_TEMPLATES[key].content(user));
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!user?.email) {
      toast.error("User email is missing");
      return;
    }
    if (!mailSubject.trim() || !mailContent.trim()) {
      toast.error("Subject and Message content are required");
      return;
    }

    setMailLoading(true);
    const token = localStorage.getItem("token");
    const payload = {
      email: user.email,
      userId: user.userId || user.id || id,
      subject: mailSubject.trim(),
      content: mailContent.trim(),
      message: mailContent.trim(),
    };

    try {
      let sent = false;
      let errorMsg = "";

      // 1. Try /users/email/warning
      try {
        const r1 = await fetch(`${baseUrl}/users/email/warning`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });
        const d1 = await r1.json().catch(() => ({}));
        if (r1.ok && (d1.status === true || d1.success === true || !d1.error)) {
          sent = true;
        } else if (d1.message) {
          errorMsg = d1.message;
        }
      } catch (e) {}

      // 2. Fallback to /auth/email/warning
      if (!sent) {
        try {
          const r2 = await fetch(`${baseUrl}/auth/email/warning`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          });
          const d2 = await r2.json().catch(() => ({}));
          if (r2.ok && (d2.status === true || d2.success === true || !d2.error)) {
            sent = true;
          } else if (d2.message) {
            errorMsg = d2.message;
          }
        } catch (e) {}
      }

      // 3. Fallback to /auth/email/pioneer
      if (!sent) {
        try {
          const r3 = await fetch(`${baseUrl}/auth/email/pioneer`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          });
          const d3 = await r3.json().catch(() => ({}));
          if (r3.ok && (d3.status === true || d3.success === true || !d3.error)) {
            sent = true;
          } else if (d3.message) {
            errorMsg = d3.message;
          }
        } catch (e) {}
      }

      // 4. Fallback to /users/send-email
      if (!sent) {
        try {
          const r4 = await fetch(`${baseUrl}/users/send-email`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          });
          const d4 = await r4.json().catch(() => ({}));
          if (r4.ok && (d4.status === true || d4.success === true || !d4.error)) {
            sent = true;
          } else if (d4.message) {
            errorMsg = d4.message;
          }
        } catch (e) {}
      }

      if (sent) {
        toast.success(`Email sent successfully to ${user.email}!`);
        setShowMailModal(false);
      } else {
        toast.error(errorMsg || "Failed to send email");
      }
    } catch (err) {
      toast.error(err.message || "Network error while dispatching email");
    } finally {
      setMailLoading(false);
    }
  };

  // Delete User Modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleConfirmDelete = async () => {
    if (!user) return;
    setDeleteLoading(true);
    const targetId = user.userId || user.id || id;
    const token = localStorage.getItem("token");

    try {
      let res = await fetch(`${baseUrl}/users/delete/${targetId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      let result = await res.json().catch(() => ({}));

      if (!res.ok || result?.status === false) {
        res = await fetch(`${baseUrl}/users/${targetId}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });
        result = await res.json().catch(() => ({}));
      }

      if (res.ok && result?.status !== false) {
        toast.success(result?.message || `User #${targetId} deleted successfully`);
        setDeleteModalOpen(false);
        setTimeout(() => {
          navigate("/user");
        }, 800);
      } else {
        toast.error(result?.message || "Failed to delete user");
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete user");
    } finally {
      setDeleteLoading(false);
    }
  };

  const fetchUser = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");

      // 1. Fetch user view from backend
      const response = await fetch(`${baseUrl}/users/view/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      const result = await response.json();

      if (result?.status === true && result?.data) {
        let userData = result.data;

        // 2. If referralUsers is not included in backend data, fallback to search in users list
        if (!userData.referralUsers && userData.referralCode) {
          try {
            const listRes = await fetch(
              `${baseUrl}/users/list?status=All&page=1&perPage=500`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              }
            );
            const listData = await listRes.json();
            if (listData?.status === true && Array.isArray(listData.data)) {
              const myCode = (userData.referralCode || "").trim().toLowerCase();
              const myUnique = (userData.uniqueCode || "").trim().toLowerCase();
              const myUser = (userData.username || "").trim().toLowerCase();

              const matching = listData.data.filter((u) => {
                const refBy = (u.referralBy || "").trim().toLowerCase();
                return (
                  refBy &&
                  (refBy === myCode ||
                    (myUnique && refBy === myUnique) ||
                    (myUser && refBy === myUser))
                );
              });

              userData.referralUsers = matching.map((u) => ({
                userId: u.userId || u.id,
                username: u.username || "—",
                email: u.email || "—",
                createdAt: u.createdAt,
                isMiningActive: !!u.isMiningActive,
                coin: parseFloat(u.coin || 0),
                status: u.status || "Active",
              }));
              userData.totalReferrals = matching.length;
              userData.activeReferrals = matching.filter(
                (u) => u.isMiningActive
              ).length;
            }
          } catch {
            // ignore fallback error
          }
        }

        setUser(userData);
      } else {
        throw new Error(result?.message || "Failed to fetch user details");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const copyCode = (code) => {
    if (!code) return;
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success("Referral Code copied!");
    setTimeout(() => setCopied(false), 2000);
  };



  const referralList = user?.referralUsers || [];
  const totalReferrals = user?.totalReferrals ?? referralList.length;
  const activeReferrals =
    user?.activeReferrals ??
    referralList.filter((r) => r.isMiningActive).length;
  const miningSessions = user?.miningSessions ?? 0;
  const avatarLetter = (user?.username || "U")[0].toUpperCase();

  // ONLY KEEP 4 TABS: Account, Mining, Referral, Devices
  const tabs = [
    { id: "Account", label: "Account", icon: FiUser },
    { id: "Mining", label: "Mining", icon: FiZap },
    { id: "Referral", label: "Referral", icon: FiShare2 },
    { id: "Devices", label: "Devices", icon: FiSmartphone },
  ];

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Toaster position="top-right" />
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <div className="p-5 sm:p-8 space-y-6 w-full">
          {/* Top Breadcrumb & Smart Back Navigation */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  if (window.history.length > 1) {
                    navigate(-1);
                  } else {
                    navigate("/user");
                  }
                }}
                className="p-2.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-xl text-gray-700 hover:text-black transition shadow-xs flex items-center justify-center cursor-pointer"
                title="Back to Previous Page"
                aria-label="Back"
              >
                <FiArrowLeft size={18} />
              </button>
              <div>
                <div className="flex items-center flex-wrap gap-1.5 text-xs text-gray-500 font-medium">
                  <Link
                    to="/user"
                    className="hover:text-black hover:underline transition flex items-center gap-1"
                  >
                    Users
                  </Link>
                  <FiChevronRight size={12} className="text-gray-400 flex-shrink-0" />
                  <span className="text-gray-900 font-bold bg-[#ffffcc] px-2 py-0.5 rounded truncate max-w-xs">
                    {user?.username ? `${user.username} (#${user.userId || id})` : `User #${id}`}
                  </span>
                </div>
                <h2 className="text-2xl font-black text-gray-800 tracking-tight mt-0.5">
                  User Details
                </h2>
              </div>
            </div>

            {user && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => fetchUser()}
                  disabled={loading}
                  className="p-2.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-xl text-gray-600 transition shadow-xs cursor-pointer"
                  title="Refresh User Data"
                >
                  <FiRefreshCw size={15} className={loading ? "animate-spin" : ""} />
                </button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-28 gap-3 text-gray-400 text-sm">
              <div className="w-8 h-8 border-4 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
              Loading user profile...
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl p-5 text-sm">
              {error}
            </div>
          ) : user ? (
            <>
              {/* ================= USER PROFILE CARD ================= */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
                  <div className="flex items-start gap-4">
                    {/* Square Blue Avatar */}
                    <div className="w-16 h-16 rounded-2xl bg-[#3b82f6] text-white flex items-center justify-center text-2xl font-bold flex-shrink-0 shadow-sm">
                      {avatarLetter}
                    </div>

                    {/* User Info */}
                    <div className="min-w-0">
                      <h2 className="text-xl font-bold text-gray-900">
                        {user.username || "—"}
                      </h2>
                      <p className="text-xs text-gray-500 mt-0.5">{user.email}</p>
                      <div className="flex items-center gap-1.5 mt-1">
                        <span className="text-xs font-mono text-gray-400 select-all">
                          {user.referralCode || user.uniqueCode || "—"}
                        </span>
                        {(user.referralCode || user.uniqueCode) && (
                          <button
                            onClick={() =>
                              copyCode(user.referralCode || user.uniqueCode)
                            }
                            className="text-gray-400 hover:text-gray-600"
                            title="Copy code"
                          >
                            {copied ? (
                              <FiCheck size={12} className="text-green-600" />
                            ) : (
                              <FiCopy size={12} />
                            )}
                          </button>
                        )}
                      </div>

                      {/* Badges */}
                      <div className="flex flex-wrap items-center gap-2 mt-2.5">
                        <span
                          className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            user.isMiningActive
                              ? "bg-emerald-50 text-emerald-700"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {user.isMiningActive
                            ? "Mining Active"
                            : "Mining Inactive"}
                        </span>

                        <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-rose-50 text-rose-600 capitalize">
                          {user.providerType || "google"}
                        </span>

                        <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-50 text-orange-600">
                          {user.strike ?? 0} Strike{user.strike === 1 ? "" : "s"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Right side: User ID & Notification Button */}
                  <div className="flex flex-row md:flex-col items-center md:items-end justify-between border-t md:border-t-0 pt-3 md:pt-0 border-gray-100 gap-2">
                    <div className="text-left md:text-right">
                      <p className="text-xs text-gray-400">User ID</p>
                      <p className="text-2xl font-black text-gray-800">
                        #{user.userId}
                      </p>
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        Joined {formatDate(user.createdAt)}
                      </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">

                      <button
                        onClick={() => setShowNotifyModal(true)}
                        className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 transition shadow-sm"
                        title="Send Push Notification to User"
                      >
                        <FiBell size={13} /> Send Notification
                      </button>
                      <button
                        onClick={handleOpenMailModal}
                        className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 transition shadow-sm"
                        title="Send Direct Email to User"
                      >
                        <FiMail size={13} /> Send Email
                      </button>
                      <button
                        onClick={() => setDeleteModalOpen(true)}
                        className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 transition shadow-sm"
                        title="Delete User Account"
                      >
                        <FiTrash2 size={13} /> Delete User
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* ================= 4 STAT CARDS ================= */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {/* Total Coins */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                  <p className="text-xs font-medium text-blue-500 uppercase tracking-wider">
                    Total Coins
                  </p>
                  <p className="text-2xl font-extrabold text-blue-600 mt-1">
                    {parseFloat(user.coin || 0).toFixed(2)}
                  </p>
                </div>

                {/* Total Mined */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                  <p className="text-xs font-medium text-emerald-500 uppercase tracking-wider">
                    Total Mined
                  </p>
                  <p className="text-2xl font-extrabold text-emerald-600 mt-1">
                    {parseFloat(user.coin || 0).toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {miningSessions} sessions
                  </p>
                </div>

                {/* Strike Count */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                  <p className="text-xs font-medium text-orange-500 uppercase tracking-wider">
                    Strike Count
                  </p>
                  <p className="text-2xl font-extrabold text-orange-500 mt-1">
                    {user.strike ?? 0}
                  </p>
                </div>

                {/* Referrals */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                  <p className="text-xs font-medium text-purple-500 uppercase tracking-wider">
                    Referrals
                  </p>
                  <p className="text-2xl font-extrabold text-purple-600 mt-1">
                    {totalReferrals}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {activeReferrals} active
                  </p>
                </div>
              </div>

              {/* ================= TAB NAVIGATION BAR (4 TABS ONLY) ================= */}
              <div className="flex items-center gap-2 overflow-x-auto pb-2">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold whitespace-nowrap transition shadow-sm ${
                        isActive
                          ? "bg-[#2563eb] text-white"
                          : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-100"
                      }`}
                    >
                      <Icon size={14} />
                      {tab.label}
                      {tab.id === "Referral" && totalReferrals > 0 && (
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                            isActive
                              ? "bg-blue-400/40 text-white"
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {totalReferrals}
                        </span>
                      )}
                    </button>
                  );
                })}
                <button
                  onClick={() => navigate(`/user/${user?.userId || id}/event-audit`)}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold whitespace-nowrap transition shadow-sm bg-white text-gray-600 hover:bg-gray-50 border border-gray-100"
                  title="View Event Activity Audit"
                >
                  <span>🏆</span> Event Activity
                </button>
              </div>

              {/* ================= TAB CONTENT: ACCOUNT ================= */}
              {activeTab === "Account" && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-5">
                  <h3 className="text-base font-bold text-gray-800">
                    Account Information
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 pt-2">
                    <Field label="User ID" value={`#${user.userId}`} />
                    <Field label="Username" value={user.username} />
                    <Field label="Email" value={user.email} />
                    <Field label="Unique Code" value={user.uniqueCode} mono />
                    <Field label="Device UUID" value={user.uuid} mono />
                    <Field
                      label="Provider"
                      value={user.providerType || "Google"}
                    />
                    <Field label="Social ID" value={user.socialId} mono />
                    <Field label="Google ID" value={user.googleId} />
                    <Field label="Apple ID" value={user.appleId} />
                    <Field
                      label="Delete Requests"
                      value={user.deleteAccountCount ?? 0}
                    />
                    <Field
                      label="Strike Count"
                      value={`${user.strike ?? 0} Strike(s)`}
                    />
                    {user.banReason && (
                      <Field label="Ban Reason" value={user.banReason} />
                    )}
                    <Field
                      label="Created At"
                      value={formatDateTime(user.createdAt)}
                    />
                    <Field
                      label="Updated At"
                      value={formatDateTime(user.updatedAt)}
                    />
                  </div>
                </div>
              )}

              {/* ================= TAB CONTENT: MINING ================= */}
              {activeTab === "Mining" && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-5">
                  <h3 className="text-base font-bold text-gray-800">
                    Mining Details
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 pt-2">
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-1">
                        Mining Status
                      </p>
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                          user.isMiningActive
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {user.isMiningActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                    <Field
                      label="Total Coins"
                      value={parseFloat(user.coin || 0).toFixed(5)}
                    />
                    <Field
                      label="Per Hour Mining Speed"
                      value={user.perHourMiningSpeed || "0"}
                    />
                    <Field
                      label="Mining Sessions"
                      value={`${miningSessions} sessions`}
                    />
                    <Field
                      label="Last Coin Update"
                      value={formatDateTime(user.lastUpdatedCoinTime)}
                    />
                  </div>
                </div>
              )}

              {/* ================= TAB CONTENT: REFERRAL ================= */}
              {activeTab === "Referral" && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-6">
                  {/* Header Title with Green Icon */}
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                      <FiShare2 size={16} />
                    </div>
                    <h3 className="text-base font-bold text-gray-800">
                      Referral
                    </h3>
                  </div>

                  {/* Summary Counters */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        Total Referrals
                      </p>
                      <p className="text-2xl font-bold text-gray-800 mt-1">
                        {totalReferrals}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        Active Referrals
                      </p>
                      <p className="text-2xl font-bold text-gray-800 mt-1">
                        {activeReferrals}
                      </p>
                    </div>
                  </div>

                  {/* Referral Users Table */}
                  <div className="overflow-x-auto rounded-xl border border-gray-100">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">
                          <th className="px-5 py-3.5">Username</th>
                          <th className="px-5 py-3.5">Email</th>
                          <th className="px-5 py-3.5">Joined</th>
                          <th className="px-5 py-3.5 text-center">Mining</th>
                          <th className="px-5 py-3.5 text-right">Coins</th>
                          <th className="px-5 py-3.5 text-center w-28">Profile</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {referralList.length === 0 ? (
                          <tr>
                            <td
                              colSpan={6}
                              className="px-5 py-12 text-center text-gray-400 text-xs"
                            >
                              <FiShare2
                                size={28}
                                className="mx-auto text-gray-300 mb-2"
                              />
                              No users have registered under this referral code yet.
                            </td>
                          </tr>
                        ) : (
                          referralList.map((refUser, idx) => (
                            <tr
                              key={refUser.userId || idx}
                              className="hover:bg-gray-50 transition"
                            >
                              <td className="px-5 py-4 font-semibold text-gray-800">
                                {refUser.username || "—"}
                              </td>
                              <td className="px-5 py-4 text-gray-600">
                                {refUser.email || "—"}
                              </td>
                              <td className="px-5 py-4 text-gray-500 text-xs whitespace-nowrap">
                                {formatDate(refUser.createdAt)}
                              </td>
                              <td className="px-5 py-4 text-center">
                                <span
                                  className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    refUser.isMiningActive
                                      ? "bg-green-100 text-green-700"
                                      : "bg-gray-100 text-gray-500"
                                  }`}
                                >
                                  {refUser.isMiningActive ? "Active" : "Inactive"}
                                </span>
                              </td>
                              <td className="px-5 py-4 text-right font-bold text-gray-700">
                                {parseFloat(refUser.coin || 0).toFixed(2)}
                              </td>
                              <td className="px-5 py-4 text-center">
                                <button
                                  onClick={() =>
                                    navigate(`/user/${refUser.userId}`)
                                  }
                                  className="p-1.5 rounded-lg bg-gray-100 hover:bg-[#ffff00] text-black transition inline-flex items-center gap-1 text-xs font-medium"
                                  title="View User Profile"
                                >
                                  <FiExternalLink size={13} /> View
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ================= TAB CONTENT: DEVICES ================= */}
              {activeTab === "Devices" && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-5">
                  <h3 className="text-base font-bold text-gray-800">
                    Registered Device Hardware
                  </h3>
                  
                  {user.deviceTokens && user.deviceTokens.length > 0 ? (
                    <div className="space-y-4">
                      {user.deviceTokens.map((device, idx) => (
                        <div key={idx} className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                          <div>
                            <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider flex items-center gap-2">
                              Device UUID {idx === 0 && <span className="bg-green-100 text-green-700 text-[10px] px-1.5 py-0.5 rounded font-bold tracking-tight">ACTIVE</span>}
                            </p>
                            <p className="font-mono text-sm font-bold text-gray-800 mt-1 select-all">
                              {device.deviceId || "Unknown"}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              Type: <span className="font-semibold text-gray-600">{device.deviceType || "Unknown"}</span> &bull; Last Seen: {formatDateTime(device.updatedAt)}
                            </p>
                          </div>
                          {device.deviceId && (
                            <button
                              onClick={() =>
                                navigate(
                                  `/duplicate-account/${encodeURIComponent(device.deviceId)}`
                                )
                              }
                              className="flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg bg-white border border-gray-200 hover:bg-[#ffff00] text-black transition shadow-sm whitespace-nowrap"
                            >
                              <FiSmartphone size={13} /> Account Group
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div>
                        <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider">
                          Latest Device UUID
                        </p>
                        <p className="font-mono text-sm font-bold text-gray-800 mt-1 select-all">
                          {user.uuid || "No hardware UUID registered"}
                        </p>
                      </div>

                      {user.uuid && (
                        <button
                          onClick={() =>
                            navigate(
                              `/duplicate-account/${encodeURIComponent(user.uuid)}`
                            )
                          }
                          className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg bg-white border border-gray-200 hover:bg-[#ffff00] text-black transition shadow-sm"
                        >
                          <FiSmartphone size={13} /> View Device Account Group
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : null}
        </div>
      </div>

      {/* Send Push Notification Modal */}
      <SendPushModal
        isOpen={showNotifyModal}
        onClose={() => setShowNotifyModal(false)}
        defaultUserId={user?.userId || id}
        defaultUsername={user?.username || user?.email}
        initialMode="particular"
      />

      {/* Send Email Modal */}
      {showMailModal && user && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto"
          onClick={() => !mailLoading && setShowMailModal(false)}
        >
          <div
            className="bg-white w-full max-w-lg rounded-2xl shadow-2xl border border-gray-100 overflow-hidden my-6"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-blue-50/80 to-indigo-50/50 border-b border-blue-100/60">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center font-bold shadow-xs">
                  <FiMail size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-800 text-base">
                    Send Email to {user.username || "User"}
                  </h3>
                  <p className="text-xs text-gray-500 font-mono">
                    {user.email} (#{user.userId || id})
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowMailModal(false)}
                disabled={mailLoading}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg transition"
              >
                <FiX size={18} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSendEmail} className="p-6 space-y-4">
              {/* Quick Template Chips */}
              <div>
                <label className="block text-[11px] uppercase font-bold text-gray-400 mb-1.5 tracking-wider">
                  Quick Templates
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(EMAIL_TEMPLATES).map(([key, tpl]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => handleApplyTemplate(key)}
                      className={`text-xs px-2.5 py-1 rounded-lg font-semibold transition border ${
                        activeTemplate === key
                          ? "bg-blue-600 text-white border-blue-600 shadow-xs"
                          : "bg-gray-50 hover:bg-gray-100 text-gray-700 border-gray-200"
                      }`}
                    >
                      {tpl.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Email Subject <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Important Notice from Zeta Network..."
                  value={mailSubject}
                  onChange={(e) => setMailSubject(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-gray-50/50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition"
                />
              </div>

              {/* Message Content */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-semibold text-gray-700">
                    Message Body <span className="text-red-500">*</span>
                  </label>
                  <span className="text-[11px] text-gray-400 font-mono">
                    {mailContent.length} chars
                  </span>
                </div>
                <textarea
                  rows={7}
                  required
                  placeholder="Write your email message here..."
                  value={mailContent}
                  onChange={(e) => setMailContent(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-gray-50/50 border border-gray-200 rounded-xl text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition resize-none leading-relaxed"
                />
              </div>

              {/* Recipient Card */}
              <div className="bg-blue-50/60 border border-blue-100 rounded-xl p-3 flex items-center justify-between text-xs text-blue-900">
                <div className="flex items-center gap-2">
                  <span className="font-bold">Recipient:</span>
                  <span className="font-mono">{user.email}</span>
                </div>
                <span className="bg-blue-100 text-blue-800 text-[10px] font-bold px-2 py-0.5 rounded-full">
                  User #{user.userId || id}
                </span>
              </div>

              {/* Modal Actions */}
              <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
                <button
                  type="button"
                  disabled={mailLoading}
                  onClick={() => setShowMailModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl transition disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={mailLoading}
                  className="flex items-center gap-2 px-5 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow transition disabled:opacity-60"
                >
                  {mailLoading ? (
                    <>
                      <FiRefreshCw size={14} className="animate-spin" /> Sending Email...
                    </>
                  ) : (
                    <>
                      <FiSend size={14} /> Send Email
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {deleteModalOpen && user && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => !deleteLoading && setDeleteModalOpen(false)}
        >
          <div
            className="bg-white w-full max-w-md rounded-2xl shadow-2xl border border-gray-100 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 text-center">
              <div className="w-14 h-14 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center mx-auto mb-4 border border-red-100">
                <FiTrash2 size={26} />
              </div>

              <h3 className="text-lg font-bold text-gray-800 mb-1">
                Delete User Account?
              </h3>
              <p className="text-xs text-gray-500 mb-4">
                Are you sure you want to permanently delete user{" "}
                <strong className="text-gray-800 font-semibold">
                  {user.username || user.email || "Account"}
                </strong>{" "}
                (#{user.userId || id})?
              </p>

              {/* User Details Summary */}
              <div className="bg-gray-50 border border-gray-100 rounded-xl p-3.5 text-xs text-left mb-4 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">User ID:</span>
                  <span className="font-bold text-gray-800">#{user.userId || id}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Username:</span>
                  <span className="font-semibold text-gray-700">{user.username || "—"}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Email:</span>
                  <span className="font-mono text-gray-700 truncate max-w-[200px]">{user.email || "—"}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Total Coins:</span>
                  <span className="font-bold text-amber-600">{parseFloat(user.coin || 0).toFixed(2)} ZX</span>
                </div>
              </div>

              <div className="bg-red-50 border border-red-100 rounded-xl p-3 text-xs text-red-700 text-left mb-5 flex items-start gap-2">
                <FiAlertCircle size={16} className="flex-shrink-0 mt-0.5 text-red-500" />
                <span>
                  This action is permanent and irreversible. The user's account, mining sessions, coins, and referral connections will be permanently removed.
                </span>
              </div>

              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  disabled={deleteLoading}
                  onClick={() => setDeleteModalOpen(false)}
                  className="w-full py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl transition disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  disabled={deleteLoading}
                  onClick={handleConfirmDelete}
                  className="w-full py-2.5 text-sm font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl shadow transition disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {deleteLoading ? (
                    <>
                      <FiRefreshCw size={14} className="animate-spin" /> Deleting...
                    </>
                  ) : (
                    <>
                      <FiTrash2 size={14} /> Yes, Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default UserDetail;
